<template>
    <UIGridContainer>
        <FrontChat />
        <UIGridRow>
            <UIGridCol cols="12" md="10" offset-md="1">
                <div class="page-article-container">
                    <div class="page-article-content">
                        <h1>{{ page.translations[0].translation.title }}</h1>
                        <ContentText :content="{ value: page.translations[0].translation.text }" />
                    </div>
                    <div class="page-article-footer d-md-flex align-items-center">
                        <h2>{{ translate('support.feedback.title') }}</h2>
                        <div v-if="showVote && !hideVote" class="button-container d-flex ml-auto">
                            <UIButton class="mr-2" @click="vote('upvote')">{{
                                translate('support.feedback.yes')
                            }}</UIButton>
                            <UIButton @click="vote('downvote')">{{ translate('support.feedback.no') }}</UIButton>
                        </div>
                        <div v-if="!showVote || hideVote" class="button-container d-flex ml-auto">
                            {{ translate('support.feedback.text') }}
                        </div>
                    </div>
                </div>
                <div class="contact">
                    <h2>{{ translate('support.contactCS') }}</h2>
                    <ServiceLinkWrapper :show-description="false" />
                </div>
            </UIGridCol>
        </UIGridRow>
    </UIGridContainer>
</template>

<script>
import { UIGridCol, UIGridRow, UIGridContainer, UIButton } from '@dundle/ui/components';
import { ServiceLinkWrapper } from '~/components/service';
import { ContentText } from '~/components/content';

import TranslationMixin from '~/mixins/TranslationMixin';
import BreadcrumbMixin from '~/mixins/BreadcrumbMixin';
import { FrontChat } from '~/components/support';

export default {
    components: {
        FrontChat,
        UIGridCol,
        UIGridRow,
        UIGridContainer,
        UIButton,
        ServiceLinkWrapper,
        ContentText,
    },

    mixins: [TranslationMixin, BreadcrumbMixin],

    async asyncData({ app, route, error }) {
        try {
            const [page, category, subcategory] = await Promise.all([
                app.$api.faq.getPage(route.params.page),
                app.$api.faq.getCategoryBySlug(route.params.category),
                app.$api.faq.getCategoryBySlug(route.params.subcategory),
            ]);

            return {
                page,
                category,
                subcategory,
            };
        } catch {
            return error({ message: 'Page not found', statusCode: 404 });
        }
    },

    data() {
        return {
            hideVote: false,
        };
    },

    head() {
        return {
            title: this.translateParams(this.page.translations?.[0].translation?.details.title),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.page.translations?.[0].translation?.details.description,
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.$locale.isTarget() && this.isCurrentLanguage ? 'index, follow' : 'noindex, nofollow',
                },
            ],
        };
    },

    computed: {
        showVote() {
            return !this.$cookies.get(`votedArticle.${this.page.id}`);
        },

        isCurrentLanguage() {
            return this.page.translations?.[0].locale.code === this.$locale.language;
        },
    },

    methods: {
        async vote(type) {
            await this.$api.faq.voteOnPage(this.page.id, this.page.translations?.[0].id, type);
            this.$cookies.set(`votedArticle.${this.page.id}`, type, {
                maxAge: 60 * 60 * 24 * 7 * 4,
            });
            this.hideVote = true;
        },
    },

    breadcrumb({ app }, data) {
        return [
            {
                name: app.$locale.regio.toLowerCase(),
                link: app.$locale.path('index'),
                url: 'https://' + app.$locale.domain + app.$locale.path('index'),
            },
            {
                name: 'Support',
                link: app.$locale.path('support'),
                url: 'https://' + app.$locale.domain + app.$locale.path('support'),
            },
            {
                name: data.category.translations?.[0].translation?.title,
                link: app.$locale.path({
                    name: 'support-category',
                    params: { category: data.category.translations?.[0].slug },
                }),
                url:
                    'https://' +
                    app.$locale.domain +
                    app.$locale.path({
                        name: 'support-category',
                        params: { category: data.category.translations?.[0].slug },
                    }),
            },
            {
                name: data.subcategory.translations?.[0].translation?.title,
                link: app.$locale.path({
                    name: 'support-category-subcategory',
                    params: {
                        category: data.category.translations?.[0].slug,
                        subcategory: data.subcategory.translations?.[0].slug,
                    },
                }),
                url:
                    'https://' +
                    app.$locale.domain +
                    app.$locale.path({
                        name: 'support-category-subcategory',
                        params: {
                            category: data.category.translations?.[0].slug,
                            subcategory: data.subcategory.translations?.[0].slug,
                        },
                    }),
            },
            {
                name: data.page.translations?.[0].translation?.title,
                link: app.$locale.path({
                    name: 'support-category-subcategory-page',
                    params: {
                        category: data.category.translations?.[0].slug,
                        subcategory: data.subcategory.translations?.[0].slug,
                        page: data.page.translations?.[0].slug,
                    },
                }),
                url:
                    'https://' +
                    app.$locale.domain +
                    app.$locale.path({
                        name: 'support-category-subcategory-page',
                        params: {
                            category: data.category.translations?.[0].slug,
                            subcategory: data.subcategory.translations?.[0].slug,
                            page: data.page.translations?.[0].slug,
                        },
                    }),
            },
        ];
    },
};
</script>

<style lang="scss" scoped>
.page-article-container {
    @media screen and (min-width: 768px) {
        border-radius: $border-radius-default;
        border: 1px solid $color-grey-3;
        .page-article-content {
            padding: 1rem 2rem 2rem;
        }
    }

    .page-article-footer {
        background: $color-grey-1;
        padding: 1rem 2rem;
        border-radius: $border-radius-default;

        @media screen and (min-width: 768px) {
            border-radius: 0 0 $border-radius-default $border-radius-default;
        }

        h2 {
            padding: 0.7rem 0;
            margin: 0;
        }

        @media screen and (max-width: 767px) {
            text-align: center;

            h2 {
                padding: 0 0 1rem 0;
            }
        }

        button {
            width: 120px;
        }
    }
}
.contact {
    margin-top: 4rem;
}
</style>

<i18n src="@dundle/locale/data/support.json"></i18n>
